/**
 * PlatformStatsPage — The Proof That It Works
 * ─────────────────────────────────────────────
 * "This semester, 47 projects crossed from Taste to Build.
 *  The longest lineage chain is 12 generations deep."
 *
 * A public-facing statistics dashboard that proves Enumé's thesis:
 * creative cross-pollination actually happens here.
 *
 * PUBLIC — no auth required. This is pitch material for educators,
 * investors, and curious visitors. It's also the page students share
 * when they want to brag about the platform's culture.
 *
 * Data sources:
 *   GET /api/platform/stats       → core metrics
 *   GET /api/platform/stats/zones → per-zone breakdown
 *   GET /api/platform/stats/leaps → recent cross-zone forks
 *
 * Place at: src/pages/PlatformStatsPage.jsx
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Layers, GitFork, Sparkles, Zap, TrendingUp, Users, ArrowRight,
  ExternalLink, Eye, Clock, ChevronRight, ArrowUpRight, Share2,
  Copy, Check, Package, Palette, Music, Utensils, BookOpen,
  Stethoscope, Cpu, BarChart3, RefreshCw, Info,
} from 'lucide-react';
import { cn } from '../utils/cn';
import { api } from '../api';
import { ZONES } from '../config/zones';

// ============================================================================
// ZONE ICONS
// ============================================================================

const ZONE_ICONS = {
  build: Package,
  beautiful: Palette,
  sound: Music,
  taste: Utensils,
  story: BookOpen,
  heal: Stethoscope,
  smart: Cpu,
  sell: TrendingUp,
};

// ============================================================================
// ANIMATED NUMBER — counts up on mount
// ============================================================================

const AnimatedNumber = ({ value, duration = 1200, suffix = '', prefix = '' }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);
  // Tracks the in-flight requestAnimationFrame id so the effect cleanup
  // can cancel it. Without this, a component that unmounts mid-count-up
  // (e.g. the user navigates away from /platform-stats while a number is
  // still animating) leaves the animate() loop running forever — each
  // frame calls setDisplay() on an unmounted component and schedules
  // another frame via requestAnimationFrame(animate), so the closure
  // (and the work it does every frame) never gets garbage collected.
  const rafIdRef = useRef(null);

  useEffect(() => {
    if (!value || hasAnimated.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const start = performance.now();
          const end = typeof value === 'number' ? value : parseFloat(value) || 0;

          const animate = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            // Ease-out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplay(Math.round(end * eased));
            if (progress < 1) {
              rafIdRef.current = requestAnimationFrame(animate);
            } else {
              rafIdRef.current = null;
            }
          };
          rafIdRef.current = requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => {
      observer.disconnect();
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [value, duration]);

  const formatted = display >= 1000
    ? display >= 1000000
      ? (display / 1000000).toFixed(1) + 'M'
      : display >= 10000
        ? (display / 1000).toFixed(0) + 'K'
        : display.toLocaleString()
    : display.toString();

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}{formatted}{suffix}
    </span>
  );
};

// ============================================================================
// HERO STAT CARD — big number with icon and label
// ============================================================================

const HeroStat = ({ icon: Icon, value, label, sublabel, color, delay = 0 }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <div
      className="relative group"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: `all 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
      }}
    >
      {/* Glow */}
      <div
        className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm"
        style={{ background: `linear-gradient(135deg, ${color}30, transparent)` }}
      />

      <div className="relative bg-zinc-900/60 backdrop-blur-sm border border-zinc-800/50 rounded-2xl p-6 hover:border-zinc-700/60 transition-colors">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
          style={{
            backgroundColor: color + '12',
            border: `1px solid ${color}25`,
          }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>

        <div className="text-3xl sm:text-4xl font-black text-white leading-none mb-1.5">
          <AnimatedNumber value={value} duration={1400} />
        </div>

        <p className="text-sm font-medium text-zinc-300">{label}</p>
        {sublabel && (
          <p className="text-xs text-zinc-500 mt-1">{sublabel}</p>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// ZONE BAR — horizontal bar with zone colors proportional to project count
// ============================================================================

const ZoneDistributionBar = ({ zones }) => {
  const total = zones.reduce((sum, z) => sum + z.project_count, 0);
  if (total === 0) return null;

  return (
    <div className="space-y-3">
      {/* The bar */}
      <div className="h-3 rounded-full overflow-hidden flex bg-zinc-800/50">
        {zones.map((z) => {
          const pct = (z.project_count / total) * 100;
          if (pct < 1) return null;
          const zone = ZONES[z.id] || {};
          return (
            <div
              key={z.id}
              className="h-full transition-all duration-700 first:rounded-l-full last:rounded-r-full"
              style={{
                width: `${pct}%`,
                backgroundColor: zone.color || '#71717a',
                minWidth: pct > 0 ? '4px' : 0,
              }}
              title={`${zone.shortLabel || z.id}: ${z.project_count} projects (${Math.round(pct)}%)`}
            />
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        {zones.filter(z => z.project_count > 0).map((z) => {
          const zone = ZONES[z.id] || {};
          const pct = Math.round((z.project_count / total) * 100);
          return (
            <div key={z.id} className="flex items-center gap-1.5">
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: zone.color || '#71717a' }}
              />
              <span className="text-xs text-zinc-400">
                <span className="font-medium text-zinc-300">{zone.shortLabel || z.id}</span>
                {' '}{z.project_count} ({pct}%)
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// ZONE CARD — individual zone with in/out flow stats
// ============================================================================

const ZoneCard = ({ zone, delay = 0 }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const zoneConfig = ZONES[zone.id] || {};
  const Icon = ZONE_ICONS[zone.id] || Sparkles;
  const totalFlow = zone.cross_zone_forks_in + zone.cross_zone_forks_out;

  return (
    <div
      ref={ref}
      className="relative group"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.97)',
        transition: `all 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
      }}
    >
      <div className="bg-zinc-900/40 border border-zinc-800/40 rounded-xl p-4 hover:border-zinc-700/50 transition-all group-hover:bg-zinc-900/60">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{
                backgroundColor: (zoneConfig.color || '#71717a') + '15',
                border: `1px solid ${(zoneConfig.color || '#71717a')}25`,
              }}
            >
              <Icon className="w-4 h-4" style={{ color: zoneConfig.color }} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white leading-none">
                {zoneConfig.shortLabel || zone.id}
              </h3>
              <p className="text-[10px] text-zinc-500 mt-0.5">{zoneConfig.label}</p>
            </div>
          </div>
          <span
            className="text-lg font-black tabular-nums"
            style={{ color: zoneConfig.color }}
          >
            {zone.project_count}
          </span>
        </div>

        {/* Flow indicators */}
        {totalFlow > 0 && (
          <div className="flex gap-3 mt-2 pt-2 border-t border-zinc-800/40">
            <div className="flex items-center gap-1 text-[11px]">
              <ArrowUpRight className="w-3 h-3 text-emerald-400" />
              <span className="text-zinc-400">
                <span className="font-semibold text-emerald-400">{zone.cross_zone_forks_out}</span> out
              </span>
            </div>
            <div className="flex items-center gap-1 text-[11px]">
              <ArrowRight className="w-3 h-3 text-blue-400 rotate-180" />
              <span className="text-zinc-400">
                <span className="font-semibold text-blue-400">{zone.cross_zone_forks_in}</span> in
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// CREATIVE LEAP CARD — a single cross-zone fork story
// ============================================================================

const LeapCard = ({ leap, index }) => {
  const navigate = useNavigate();
  const parentZone = ZONES[leap.parent?.zone] || {};
  const childZone = ZONES[leap.child?.zone] || {};

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div
      className="group relative bg-zinc-900/40 border border-zinc-800/40 rounded-xl p-4 hover:border-zinc-700/50 hover:bg-zinc-900/60 transition-all cursor-pointer"
      onClick={() => navigate(`/dashboard?project=${leap.child.id}`)}
      style={{
        animationDelay: `${index * 80}ms`,
      }}
    >
      {/* Zone transition badge */}
      <div className="flex items-center gap-2 mb-3">
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
          style={{
            color: parentZone.color || '#a1a1aa',
            backgroundColor: (parentZone.color || '#71717a') + '12',
            border: `1px solid ${(parentZone.color || '#71717a')}25`,
          }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: parentZone.color }} />
          {parentZone.shortLabel || leap.parent?.zone}
        </span>

        <ArrowRight className="w-3 h-3 text-zinc-600" />

        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
          style={{
            color: childZone.color || '#a1a1aa',
            backgroundColor: (childZone.color || '#71717a') + '12',
            border: `1px solid ${(childZone.color || '#71717a')}25`,
          }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: childZone.color }} />
          {childZone.shortLabel || leap.child?.zone}
        </span>

        <span className="text-[10px] text-zinc-600 ml-auto">
          {timeAgo(leap.child?.created_at)}
        </span>
      </div>

      {/* Project names */}
      <div className="flex items-center gap-2">
        {/* Parent thumbnail */}
        <div className="flex-shrink-0">
          {leap.parent?.cover_image_url ? (
            <img
              src={leap.parent.cover_image_url}
              alt=""
              className="w-10 h-10 rounded-lg object-cover border border-zinc-800"
            />
          ) : (
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center border border-zinc-800"
              style={{ backgroundColor: (parentZone.color || '#71717a') + '10' }}
            >
              <span className="text-xs font-bold" style={{ color: parentZone.color }}>
                {(leap.parent?.name || '?')[0]}
              </span>
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-xs text-zinc-500 truncate">
            <span className="font-medium text-zinc-400">{leap.parent?.name || 'Untitled'}</span>
            {' by '}
            {leap.parent?.creator_username || 'unknown'}
          </p>
          <div className="flex items-center gap-1 mt-0.5">
            <Sparkles className="w-3 h-3 text-fuchsia-400 flex-shrink-0" />
            <p className="text-sm font-medium text-white truncate">
              {leap.child?.name || 'Untitled'}
            </p>
          </div>
          <p className="text-[10px] text-zinc-500 mt-0.5">
            by {leap.child?.creator_username || 'unknown'}
          </p>
        </div>

        <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-zinc-500 transition-colors flex-shrink-0" />
      </div>
    </div>
  );
};

// ============================================================================
// ZONE FLOW VISUALIZATION — chord-style connections between zones
// ============================================================================

const ZoneFlowViz = ({ zonePairs }) => {
  if (!zonePairs || zonePairs.length === 0) return null;

  const zoneOrder = ['build', 'beautiful', 'sound', 'taste', 'story', 'heal', 'smart', 'sell'];
  const size = 280;
  const center = size / 2;
  const radius = 105;

  // Position zones in a circle
  const zonePositions = {};
  zoneOrder.forEach((zoneId, i) => {
    const angle = (i / zoneOrder.length) * Math.PI * 2 - Math.PI / 2;
    zonePositions[zoneId] = {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle),
      angle,
    };
  });

  // Map zone labels to IDs
  const labelToId = {};
  Object.entries(ZONES).forEach(([id, z]) => {
    labelToId[z.label] = id;
    labelToId[z.shortLabel] = id;
    labelToId[id] = id;
  });

  // Deduplicate and resolve zone pairs
  const connections = [];
  const seen = new Set();
  zonePairs.forEach(pair => {
    const fromId = labelToId[pair.from] || pair.from;
    const toId = labelToId[pair.to] || pair.to;
    const key = [fromId, toId].sort().join('-');
    if (!seen.has(key) && zonePositions[fromId] && zonePositions[toId]) {
      seen.add(key);
      connections.push({ from: fromId, to: toId });
    }
  });

  return (
    <div className="flex justify-center">
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[280px]">
        {/* Connection arcs */}
        {connections.map(({ from, to }, i) => {
          const p1 = zonePositions[from];
          const p2 = zonePositions[to];
          const fromColor = ZONES[from]?.color || '#71717a';
          const toColor = ZONES[to]?.color || '#71717a';
          const gradId = `flow-grad-${i}`;

          // Quadratic bezier through center
          const mx = center + (p1.x + p2.x - 2 * center) * 0.15;
          const my = center + (p1.y + p2.y - 2 * center) * 0.15;

          return (
            <g key={`${from}-${to}`}>
              <defs>
                <linearGradient id={gradId} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor={fromColor} stopOpacity="0.5" />
                  <stop offset="50%" stopColor={fromColor} stopOpacity="0.15" />
                  <stop offset="100%" stopColor={toColor} stopOpacity="0.5" />
                </linearGradient>
              </defs>
              <path
                d={`M ${p1.x} ${p1.y} Q ${mx} ${my} ${p2.x} ${p2.y}`}
                fill="none"
                stroke={`url(#${gradId})`}
                strokeWidth="1.5"
                className="transition-opacity duration-300"
              />
            </g>
          );
        })}

        {/* Zone nodes */}
        {zoneOrder.map((zoneId) => {
          const pos = zonePositions[zoneId];
          const zone = ZONES[zoneId] || {};
          const hasConnection = connections.some(c => c.from === zoneId || c.to === zoneId);

          return (
            <g key={zoneId}>
              {/* Outer glow */}
              {hasConnection && (
                <circle
                  cx={pos.x} cy={pos.y} r="18"
                  fill={zone.color || '#71717a'}
                  opacity="0.08"
                />
              )}
              {/* Node */}
              <circle
                cx={pos.x} cy={pos.y} r="12"
                fill={`${(zone.color || '#71717a')}20`}
                stroke={zone.color || '#71717a'}
                strokeWidth="1.5"
                strokeOpacity={hasConnection ? 0.6 : 0.2}
              />
              {/* Label */}
              <text
                x={pos.x}
                y={pos.y + 24}
                textAnchor="middle"
                fill={zone.color || '#a1a1aa'}
                fontSize="8"
                fontWeight="600"
                opacity={hasConnection ? 0.8 : 0.4}
              >
                {zone.shortLabel || zoneId}
              </text>
              {/* Abbreviation inside */}
              <text
                x={pos.x}
                y={pos.y + 3.5}
                textAnchor="middle"
                fill={zone.color || '#a1a1aa'}
                fontSize="7"
                fontWeight="700"
                opacity={0.7}
              >
                {(zone.shortLabel || zoneId).slice(0, 2).toUpperCase()}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// ============================================================================
// SHARE BUTTON
// ============================================================================

const ShareButton = () => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* fallback: do nothing */ }
  };

  return (
    <button
      onClick={handleCopy}
      className={cn(
        'inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all',
        copied
          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
          : 'bg-zinc-800/60 text-zinc-300 border border-zinc-700/40 hover:bg-zinc-800 hover:border-zinc-600',
      )}
    >
      {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
      {copied ? 'Link copied!' : 'Share stats'}
    </button>
  );
};

// ============================================================================
// MAIN PAGE
// ============================================================================

const PlatformStatsPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [zones, setZones] = useState([]);
  const [leaps, setLeaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Fetch all data in parallel ──────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      setLoading(true);
      setError(null);

      try {
        const [statsRes, zonesRes, leapsRes] = await Promise.allSettled([
          api.get('/platform/stats'),
          api.get('/platform/stats/zones'),
          api.get('/platform/stats/leaps', { params: { limit: 12, days: 90 } }),
        ]);

        if (cancelled) return;

        if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
        if (zonesRes.status === 'fulfilled') setZones(zonesRes.value.data?.zones || []);
        if (leapsRes.status === 'fulfilled') setLeaps(leapsRes.value.data?.leaps || []);

        // If all failed, show error
        if (
          statsRes.status === 'rejected' &&
          zonesRes.status === 'rejected' &&
          leapsRes.status === 'rejected'
        ) {
          setError('Could not load platform statistics');
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load statistics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, []);

  // ── Loading state ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-12 h-12 border-2 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
            <Sparkles className="w-5 h-5 text-purple-400 absolute inset-0 m-auto" />
          </div>
          <p className="text-zinc-500 text-sm">Crunching the numbers...</p>
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────
  if (error && !stats) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center">
            <BarChart3 className="w-8 h-8 text-zinc-600" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Stats unavailable</h1>
          <p className="text-zinc-400 text-sm mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 text-zinc-300 rounded-xl text-sm font-medium hover:bg-zinc-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Try again
          </button>
        </div>
      </div>
    );
  }

  const crossZonePct = stats?.cross_zone_percentage || 0;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* ── Ambient background ────────────────────────────────────── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-purple-600/[0.03] blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-indigo-600/[0.03] blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full bg-fuchsia-600/[0.02] blur-3xl" />
      </div>

      {/* ── Header ─────────────────────────────────────────────────── */}
      <header className="relative border-b border-zinc-800/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
          >
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-sm font-semibold">Enumé</span>
          </Link>

          <ShareButton />
        </div>
      </header>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
        {/* ── Hero ───────────────────────────────────────────────────── */}
        <section className="pt-12 sm:pt-16 pb-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-fuchsia-500/8 border border-fuchsia-500/15 text-fuchsia-300 text-xs font-semibold mb-5">
              <Zap className="w-3.5 h-3.5" />
              Live Platform Stats
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black leading-[1.1] tracking-tight mb-4">
              Creative cross&#8209;pollination,{' '}
              <span className="bg-gradient-to-r from-fuchsia-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
                measured
              </span>
            </h1>

            <p className="text-base sm:text-lg text-zinc-400 leading-relaxed max-w-lg">
              Enumé tracks how ideas jump between creative disciplines.
              Here's the proof that unexpected connections actually happen.
            </p>
          </div>
        </section>

        {/* ── Hero stats grid ────────────────────────────────────────── */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 pb-12">
          <HeroStat
            icon={Layers}
            value={stats?.total_projects || 0}
            label="Projects"
            sublabel="created on the platform"
            color="#a855f7"
            delay={0}
          />
          <HeroStat
            icon={GitFork}
            value={stats?.total_forks || 0}
            label="Forks"
            sublabel="ideas building on ideas"
            color="#6366f1"
            delay={100}
          />
          <HeroStat
            icon={Sparkles}
            value={stats?.cross_zone_forks || 0}
            label="Cross-Zone Leaps"
            sublabel="ideas that jumped disciplines"
            color="#ec4899"
            delay={200}
          />
          <HeroStat
            icon={Users}
            value={stats?.total_users || 0}
            label="Creators"
            sublabel="building and forking"
            color="#06b6d4"
            delay={300}
          />
        </section>

        {/* ── Big insight callout ─────────────────────────────────────── */}
        {crossZonePct > 0 && (
          <section className="pb-12">
            <div className="relative overflow-hidden rounded-2xl border border-fuchsia-500/15 bg-gradient-to-br from-fuchsia-500/[0.06] via-purple-500/[0.04] to-indigo-500/[0.06]">
              <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-fuchsia-500/[0.05] blur-3xl" />
              <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-fuchsia-500/10 border border-fuchsia-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl sm:text-3xl font-black text-fuchsia-400">
                      <AnimatedNumber value={Math.round(crossZonePct)} suffix="%" duration={1600} />
                    </span>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-white">
                      of all forks cross creative disciplines
                    </h2>
                    <p className="text-sm text-zinc-400 mt-1">
                      {stats?.zones_connected || 0} unique zone-to-zone connections discovered
                      {stats?.deepest_chain > 1 && (
                        <> &mdash; the deepest lineage chain runs <span className="font-semibold text-cyan-400">{stats.deepest_chain} generations</span> deep</>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ── Two-column: Zone breakdown + Flow viz ───────────────────── */}
        <section className="pb-12">
          <div className="grid lg:grid-cols-5 gap-6">
            {/* Zone breakdown */}
            <div className="lg:col-span-3">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-4 h-4 text-zinc-500" />
                <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider">
                  Projects by Zone
                </h2>
              </div>

              {/* Distribution bar */}
              <div className="mb-5">
                <ZoneDistributionBar zones={zones} />
              </div>

              {/* Zone cards grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {zones.map((z, i) => (
                  <ZoneCard key={z.id} zone={z} delay={i * 60} />
                ))}
              </div>
            </div>

            {/* Flow visualization */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-4 h-4 text-zinc-500" />
                <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider">
                  Zone Connections
                </h2>
              </div>

              <div className="bg-zinc-900/40 border border-zinc-800/40 rounded-xl p-4">
                {stats?.unique_zone_pairs?.length > 0 ? (
                  <>
                    <ZoneFlowViz zonePairs={stats.unique_zone_pairs} />
                    <p className="text-center text-[11px] text-zinc-500 mt-3">
                      Each line represents a creative leap between zones
                    </p>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Sparkles className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                    <p className="text-sm text-zinc-500">
                      Cross-zone connections will appear here as creators fork across disciplines
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── Recent creative leaps ──────────────────────────────────── */}
        {leaps.length > 0 && (
          <section className="pb-16">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-fuchsia-400" />
                <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider">
                  Recent Creative Leaps
                </h2>
              </div>
              <span className="text-xs text-zinc-600">Last 90 days</span>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {leaps.map((leap, i) => (
                <LeapCard key={`${leap.parent?.id}-${leap.child?.id}`} leap={leap} index={i} />
              ))}
            </div>
          </section>
        )}

        {/* ── Additional stats ────────────────────────────────────────── */}
        <section className="pb-12">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="bg-zinc-900/40 border border-zinc-800/40 rounded-xl p-5 text-center">
              <div className="text-2xl font-black text-cyan-400 mb-1">
                <AnimatedNumber value={stats?.deepest_chain || 0} duration={1000} />
              </div>
              <p className="text-xs text-zinc-400">Deepest lineage chain</p>
              <p className="text-[10px] text-zinc-600 mt-1">generations of creative evolution</p>
            </div>
            <div className="bg-zinc-900/40 border border-zinc-800/40 rounded-xl p-5 text-center">
              <div className="text-2xl font-black text-amber-400 mb-1">
                <AnimatedNumber value={stats?.avg_fork_depth || 0} duration={1000} />
              </div>
              <p className="text-xs text-zinc-400">Average fork depth</p>
              <p className="text-[10px] text-zinc-600 mt-1">ideas tend to go this many layers deep</p>
            </div>
            <div className="bg-zinc-900/40 border border-zinc-800/40 rounded-xl p-5 text-center">
              <div className="text-2xl font-black text-emerald-400 mb-1">
                <AnimatedNumber value={stats?.active_this_month || 0} duration={1000} />
              </div>
              <p className="text-xs text-zinc-400">Active this month</p>
              <p className="text-[10px] text-zinc-600 mt-1">new projects in the last 30 days</p>
            </div>
          </div>
        </section>

        {/* ── CTA ─────────────────────────────────────────────────────── */}
        <section className="pb-16 sm:pb-20">
          <div className="relative overflow-hidden rounded-2xl border border-purple-500/15 bg-gradient-to-br from-purple-500/[0.06] to-indigo-500/[0.06]">
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute right-0 top-0 w-96 h-96 rounded-full bg-purple-600/[0.05] blur-3xl" />
            </div>

            <div className="relative p-8 sm:p-10 text-center">
              <h2 className="text-2xl sm:text-3xl font-black mb-3 leading-tight">
                See the connections for yourself
              </h2>
              <p className="text-zinc-400 text-sm sm:text-base max-w-md mx-auto mb-8 leading-relaxed">
                Every project on Enumé is a node in a living constellation.
                Fork something unexpected. Start a lineage chain. Cross a zone.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/register"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-xl font-semibold text-sm transition-all shadow-xl shadow-purple-600/25 hover:shadow-purple-500/35"
                >
                  <Sparkles className="w-4 h-4" />
                  Join Enumé
                </Link>
                <Link
                  to="/dashboard"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl font-semibold text-sm transition-colors border border-zinc-700/50"
                >
                  Explore the Map
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ── Footer ─────────────────────────────────────────────────── */}
      <footer className="relative border-t border-zinc-800/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
              <Sparkles className="w-2.5 h-2.5 text-white" />
            </div>
            <span className="text-xs text-zinc-500">
              Enumé &mdash; Where ideas cross-pollinate
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors">Terms</Link>
            <Link to="/privacy" className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors">Privacy</Link>
            <Link to="/dmca" className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors">DMCA</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PlatformStatsPage;